import React, { ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../utils/cn";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends React.Component<Props & { t: (key: string) => string }, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
          <div className="bg-white p-8 rounded-xl shadow-xl max-w-lg w-full border border-red-100">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4 text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('errorBoundary.title')}</h2>
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
              {t('errorBoundary.message')}
            </p>

            {this.state.error && (
              <div className="bg-gray-100 p-4 rounded-lg overflow-auto text-xs font-mono text-red-800 mb-6 max-h-40 border border-gray-200">
                <strong>{t('errorboundary.error')}</strong> {this.state.error.toString()}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  "flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-bold",
                  "hover:bg-blue-700 transition-colors shadow-sm"
                )}
              >
                {t('errorBoundary.reload')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return <>{this.props.children}</>;
  }
}

// Wrapper to use hooks with class component
export const ErrorBoundary: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  return <ErrorBoundaryClass {...props} t={t} />;
};